import {apiClient} from 'common/util/api-client';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {objToQueryString} from 'common/util';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {MPProperties} from 'common/constants';
import {ResourceType} from 'common/report/constants';

import {appApiUrl, queryApiUrl} from '../utils';
import {User} from './types';

interface Column {
  value: string;
  resourceType: string;
}

export async function fetchUsersCount({
  cohort,
  includeAllUsers,
  abortSignal = null,
  useQuerySampling = false,
}: {
  cohort: Cohort;
  includeAllUsers: boolean;
  abortSignal?: AbortSignal;
  useQuerySampling?: boolean;
}): Promise<number> {
  const url = queryApiUrl(`engage/stats`);
  const reqBody = {
    filter_by_cohort: JSON.stringify({
      raw_cohort: cohort.serialize(),
    }),
    ...(cohort.dataGroupId ? {data_group_id: cohort.dataGroupId} : {}),
    workspace_id: getWorkspaceId(),
    project_id: getProjectId(),
    include_all_users: includeAllUsers,
    segment_by_cohorts: JSON.stringify({
      webhook: true,
    }),
    use_query_sampling: useQuerySampling,
    enable_upsampling: useQuerySampling,
  };
  // TODO: this is a weird way to get total count of users, but it is how backbone does it currently
  const {webhook} = await apiClient.post<{webhook: number}>(url, objToQueryString(reqBody), {
    headers: {
      'Content-Type': `application/x-www-form-urlencoded`,
    },
    abortSignal,
  });
  return webhook;
}

export async function fetchUsers({
  cohort,
  includeAllUsers,
  columns,
  abortSignal = null,
  search = ``,
  sortOrder = `descending`,
  sortColumn = {
    value: MPProperties.LastSeen,
    resourceType: ResourceType.User,
  },
  useQuerySampling = false,
}: {
  cohort: Cohort;
  includeAllUsers: boolean;
  columns: Column[];
  abortSignal?: AbortSignal;
  search?: string;
  sortOrder?: string;
  sortColumn?: Column;
  useQuerySampling?: boolean;
}) {
  const url = queryApiUrl(`engage`);
  const reqBody = {
    filter_by_cohort: JSON.stringify({
      raw_cohort: cohort.serialize(),
    }),
    workspace_id: getWorkspaceId(),
    sort_order: sortOrder,
    sort_column: JSON.stringify(sortColumn),
    search,
    columns: JSON.stringify(columns),
    // https://www.notion.so/mxpnl/Query-tracking-spec-da125612f4b04f24b9823dc7d28232c3
    tracking_props: JSON.stringify({
      is_main_query_for_report: false,
      report_name: `users_sidebar`,
    }),
    project_id: getProjectId(),
    include_all_users: includeAllUsers,
    limit: 1000,
    ...(cohort.dataGroupId ? {data_group_id: cohort.dataGroupId} : {}),
    use_query_sampling: useQuerySampling,
  };
  return apiClient.post<User[]>(url, objToQueryString(reqBody), {
    headers: {
      'Content-Type': `application/x-www-form-urlencoded`,
    },
    abortSignal,
  });
}

export async function downloadCSV({
  includeAllUsers,
  queryHref,
  search,
  columns,
  cohort,
}: {
  queryHref: string;
  cohort: Cohort;
  search?: string;
  columns: Column[];
  includeAllUsers: boolean;
}): Promise<string> {
  const url = appApiUrl(`export_csv`, {projectId: getProjectId(), workspaceId: getWorkspaceId()});
  const reqBody = {
    query_href: queryHref,
    columns,
    filter_by_cohort: {
      raw_cohort: cohort.serialize(),
    },
    search,
    include_all_users: includeAllUsers,
    ...(cohort.dataGroupId ? {data_group_id: cohort.dataGroupId} : {}),
  };
  const response = await apiClient.post<string>(url, JSON.stringify(reqBody));
  const {results} = JSON.parse(response);
  if (!results) {
    throw new Error(`No results found`);
  }
  return results.url;
}
